/**
 * Profile — perfil comercial Cloud API.
 * Edita about, description, address, email, vertical, websites.
 * Mostra profile_picture_url read-only.
 */

import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { IconUserCircle, IconLink, IconBuildingStore } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import WhatsAppCloudApiService from '../../../../../WhatsAppCloudApi/services/WhatsAppCloudApiService';

const VERTICAL_OPTIONS = [
  { value: 'AUTO', label: 'Automotivo' },
  { value: 'BEAUTY', label: 'Beleza' },
  { value: 'APPAREL', label: 'Vestuário' },
  { value: 'EDU', label: 'Educação' },
  { value: 'ENTERTAIN', label: 'Entretenimento' },
  { value: 'EVENT_PLAN', label: 'Eventos' },
  { value: 'FINANCE', label: 'Finanças' },
  { value: 'GROCERY', label: 'Mercado' },
  { value: 'GOVT', label: 'Governo' },
  { value: 'HOTEL', label: 'Hotéis' },
  { value: 'HEALTH', label: 'Saúde' },
  { value: 'NONPROFIT', label: 'ONG' },
  { value: 'PROF_SERVICES', label: 'Serviços profissionais' },
  { value: 'RETAIL', label: 'Varejo' },
  { value: 'TRAVEL', label: 'Viagem' },
  { value: 'RESTAURANT', label: 'Restaurante' },
  { value: 'NOT_A_BIZ', label: 'Não é negócio' },
  { value: 'OTHER', label: 'Outro' },
];

const Field = ({ label, children, hint, charCount, maxLength }) => (
  <div className="cloudapi-field">
    <label className="cloudapi-field__label">
      {label}
      {maxLength && (
        <span style={{ float: 'right', fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>
          {charCount || 0}/{maxLength}
        </span>
      )}
    </label>
    {children}
    {hint && <p className="cloudapi-field__hint">{hint}</p>}
  </div>
);

const Profile = ({ agentId }) => {
  const [profile, setProfile] = useState({
    about: '',
    description: '',
    address: '',
    email: '',
    vertical: '',
    websites: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await WhatsAppCloudApiService.getProfile(agentId);
        if (cancelled) return;
        const data = resp.data || {};
        setProfile({
          about: data.about || '',
          description: data.description || '',
          address: data.address || '',
          email: data.email || '',
          vertical: data.vertical || '',
          websites: Array.isArray(data.websites) ? data.websites : [],
          profile_picture_url: data.profile_picture_url || '',
        });
      } catch (err) {
        console.error('[CloudApi Profile] erro:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const update = (key, value) => setProfile((prev) => ({ ...prev, [key]: value }));

  const updateWebsite = (idx, value) => {
    const next = [...(profile.websites || [])];
    next[idx] = value;
    update('websites', next);
  };

  const addWebsite = () => {
    if ((profile.websites || []).length >= 2) {
      Swal.fire({
        icon: 'info',
        title: 'Limite Meta',
        text: 'A Meta permite no máximo 2 websites.',
      });
      return;
    }
    update('websites', [...(profile.websites || []), '']);
  };

  const removeWebsite = (idx) => {
    update(
      'websites',
      (profile.websites || []).filter((_, i) => i !== idx),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      // Limpa websites vazios
      payload.websites = (payload.websites || []).filter((w) => w && w.trim());
      await WhatsAppCloudApiService.updateProfile(agentId, payload);
      Swal.fire({
        icon: 'success',
        title: 'Perfil atualizado!',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao atualizar',
        text: err.response?.data?.error || 'Tente novamente.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div>
      <div className="cloudapi-section">
        <h4 className="cloudapi-section__title">
          <IconUserCircle size={16} />
          Identidade
        </h4>

        {profile.profile_picture_url && (
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={profile.profile_picture_url}
              alt="Foto de perfil"
              style={{ width: 64, height: 64, borderRadius: 12, objectFit: 'cover' }}
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Foto de perfil (gerencie no Meta Business)
            </span>
          </div>
        )}

        <Field
          label="Sobre"
          maxLength={139}
          charCount={(profile.about || '').length}
          hint="Texto curto que aparece no perfil do WhatsApp"
        >
          <textarea
            className="cloudapi-field__input"
            rows={2}
            maxLength={139}
            value={profile.about || ''}
            onChange={(e) => update('about', e.target.value)}
          />
        </Field>

        <Field
          label="Descrição"
          maxLength={512}
          charCount={(profile.description || '').length}
        >
          <textarea
            className="cloudapi-field__input"
            rows={4}
            maxLength={512}
            value={profile.description || ''}
            onChange={(e) => update('description', e.target.value)}
          />
        </Field>
      </div>

      <div className="cloudapi-section">
        <h4 className="cloudapi-section__title">
          <IconBuildingStore size={16} />
          Negócio
        </h4>

        <Field
          label="Vertical"
          hint="Categoria do seu negócio segundo a Meta"
        >
          <select
            className="cloudapi-field__input"
            value={
              VERTICAL_OPTIONS.some((o) => o.value === profile.vertical)
                ? profile.vertical
                : ''
            }
            onChange={(e) => update('vertical', e.target.value)}
          >
            <option value="" disabled>
              Selecione uma opção...
            </option>
            {VERTICAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Endereço" maxLength={256} charCount={(profile.address || '').length}>
          <input
            type="text"
            className="cloudapi-field__input"
            maxLength={256}
            value={profile.address || ''}
            onChange={(e) => update('address', e.target.value)}
          />
        </Field>

        <Field label="Email">
          <input
            type="email"
            className="cloudapi-field__input"
            value={profile.email || ''}
            onChange={(e) => update('email', e.target.value)}
          />
        </Field>
      </div>

      <div className="cloudapi-section">
        <h4 className="cloudapi-section__title">
          <IconLink size={16} />
          Websites (até 2)
        </h4>
        {(profile.websites || []).map((url, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="url"
              className="cloudapi-field__input"
              value={url}
              placeholder="https://exemplo.com"
              onChange={(e) => updateWebsite(idx, e.target.value)}
            />
            <button
              type="button"
              onClick={() => removeWebsite(idx)}
              className="cloudapi-hero__action-btn cloudapi-hero__action-btn--danger"
              style={{ padding: '0 10px' }}
            >
              Remover
            </button>
          </div>
        ))}
        {(profile.websites || []).length < 2 && (
          <button
            type="button"
            onClick={addWebsite}
            className="cloudapi-hero__action-btn"
            style={{ marginTop: 4 }}
          >
            + Adicionar website
          </button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 20px',
            background: '#1877F2',
            border: 'none',
            borderRadius: 8,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? <Spinner animation="border" size="sm" /> : 'Salvar Perfil'}
        </button>
      </div>
    </div>
  );
};

export default Profile;
